import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import { removeItems } from '@/utils'
import type { Project, ProjectDTO } from './types'

interface ProjectsStoreActions {
  reset: () => void
  startLoadingProjects: () => void
  stopLoadingProjects: () => void
  setProjects: (
    projects: Project[] | ProjectDTO[],
    folderId: null | number,
  ) => void
  addProject: (project: Project | ProjectDTO) => void
  deleteProjects: (ids: number[]) => void
}

type ProjectsStoreMutations = {
  createProject: (body: {
    name: string
    folder_id?: number
  }) => Promise<Project>
  deleteProjects: (projects: Project[]) => Promise<void>
  archiveProjects: (projects: Project[]) => Promise<void>
  unarchiveProjects: (projects: Project[]) => Promise<void>
  duplicateProject: (slug: string) => Promise<Project>
}

interface ProjectsStoreLoaders {
  fetchProjects: (
    folderId?: null | number,
    options?: { allFolders?: boolean },
  ) => Promise<void>
  fetchArchivedProjects: () => Promise<void>
}

interface ProjectsStore {
  loadingProjects: boolean
  loadingArchivedProjects: boolean
  mutating: boolean
  folderId: null | number
  projects: Project[]
  archivedProjects: Project[]
  actions: ProjectsStoreActions
  mutations: ProjectsStoreMutations
  loaders: ProjectsStoreLoaders
}

const firstFailure = async (
  projects: Project[],
  action: 'archive' | 'unarchive',
) => {
  const results = await Promise.allSettled(
    projects.map(({ slug }) => executePost(`/projects/${slug}/${action}`)),
  )
  return results.find((result) => result.status === 'rejected')
}

export const useProjectsStore = create<ProjectsStore>((set, get, store) => ({
  loadingProjects: true,
  loadingArchivedProjects: true,
  mutating: false,
  folderId: null,
  projects: [],
  archivedProjects: [],
  actions: {
    reset: () => set(store.getInitialState()),
    startLoadingProjects: () => set({ loadingProjects: true }),
    stopLoadingProjects: () => set({ loadingProjects: false }),
    setProjects: (projects, folderId) =>
      set({ projects: camelcaseKeys(projects), folderId }),
    addProject: (project) =>
      set((state) => ({
        projects: [camelcaseKeys(project), ...state.projects],
      })),
    deleteProjects: (ids) =>
      set((state) => ({
        projects: removeItems(state.projects, ids),
        archivedProjects: removeItems(state.archivedProjects, ids),
      })),
  },
  mutations: {
    createProject: async (body) => {
      set({ mutating: true })
      const response = await executePost(`/projects`, { ...body }).finally(() =>
        set({ mutating: false }),
      )
      const project: Project = await response.json()
      get().actions.addProject(project)

      return project
    },

    deleteProjects: async (projects) => {
      const { deleteProjects, addProject } = get().actions
      deleteProjects(projects.map(({ id }) => id))

      try {
        await Promise.all(
          projects.map(({ slug }) => executeDelete(`/projects/${slug}`)),
        )
      } catch (error) {
        projects.filter(({ archivedAt }) => !archivedAt).forEach(addProject)
        if (projects.some(({ archivedAt }) => archivedAt)) {
          await get().loaders.fetchArchivedProjects()
        }
        throw error
      }
    },

    archiveProjects: async (projects) => {
      get().actions.deleteProjects(projects.map(({ id }) => id))
      const failure = await firstFailure(projects, 'archive')
      if (!failure) return

      await get().loaders.fetchProjects(get().folderId)
      throw failure.reason
    },

    unarchiveProjects: async (projects) => {
      get().actions.deleteProjects(projects.map(({ id }) => id))
      const failure = await firstFailure(projects, 'unarchive')
      if (!failure) return

      await get().loaders.fetchArchivedProjects()
      throw failure.reason
    },

    duplicateProject: async (slug) => {
      set({ mutating: true })
      const response = await executePost(`/projects/${slug}/duplicate`).finally(
        () => set({ mutating: false }),
      )
      return camelcaseKeys(await response.json()) as Project
    },
  },

  loaders: {
    fetchProjects: async (folderId = null, options = {}) => {
      const { folderId: storedFolderId, projects } = get()
      const { startLoadingProjects, setProjects, stopLoadingProjects } =
        get().actions

      const allFolders = options.allFolders ?? false

      if (allFolders || storedFolderId !== folderId || projects.length === 0) {
        startLoadingProjects()
      }

      const queryParams: Record<string, string | number | boolean> = {}
      if (allFolders) {
        queryParams.all = 'true'
      } else if (folderId) {
        queryParams.folder_id = folderId
      }

      const response = await executeGet(`/projects`, { queryParams })
      const result = await response.json()

      setProjects(result, folderId)
      stopLoadingProjects()
    },

    fetchArchivedProjects: async () => {
      set({ loadingArchivedProjects: true })
      const response = await executeGet(`/projects`, {
        queryParams: { archived: 'true' },
      })
      set({
        archivedProjects: camelcaseKeys(await response.json()),
        loadingArchivedProjects: false,
      })
    },
  },
}))
